// @ts-nocheck
/**
 * Trunk form fields — validation preserved (IPv4, port 1024-65535,
 * pilot 10 digits, channels 0-10000, cps 0-1000).
 */
import { useState, useEffect, useMemo } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  API_CODECS_LIST,
  API_OPERATORS_LIST,
  API_SERVER_MANAGEMENT_LIST,
  API_COUNTRY_CODES_LIST,
} from '@/service/url';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils/cn';
import { MultiSelect } from '@/components/ui/multi-select';
import { getApiCall } from '@/service/httpCall';
import {
  TRUNK_AUTH_TYPES,
  TRUNK_AUTH_TYPE_IP,
  TRUNK_AUTH_TYPE_REGISTRATION,
} from '@/utilities/constant';

const _requiredStar = <span style={{ color: 'red', margin: '0 4px' }}>*</span>;

// Trunk type
export const TRUNK_TYPE_DOMESTIC = 0;
export const TRUNK_TYPE_INTERNATIONAL = 1;
export const TRUNK_DOMESTIC_COUNTRY_CODE = 'IN';

const formatCallingCode = (code: any) => {
  const digits = String(code ?? '').trim().replace(/^\+/, '');
  return digits ? `+${digits}` : '';
};

export const TRUNK_PREFIX_FIELDS = [
  { key: 'incoming_caller_from_prefix', label: 'Incoming Caller From Prefix' },
  { key: 'incoming_caller_to_prefix', label: 'Incoming Caller To Prefix' },
  { key: 'outgoing_caller_from_prefix', label: 'Outgoing Caller From Prefix' },
  { key: 'outgoing_caller_to_prefix', label: 'Outgoing Caller To Prefix' },
];


const TRUNK_PREFIX_GROUPS = [
  {
    title: 'Incoming',
    fields: [
      { key: 'incoming_caller_from_prefix', short: 'From Prefix' },
      { key: 'incoming_caller_to_prefix', short: 'To Prefix' },
    ],
  },
  {
    title: 'Outgoing',
    fields: [
      { key: 'outgoing_caller_from_prefix', short: 'From Prefix' },
      { key: 'outgoing_caller_to_prefix', short: 'To Prefix' },
    ],
  },
];

export const pickTrunkPrefixes = (src: any = {}) =>
  Object.fromEntries(TRUNK_PREFIX_FIELDS.map((f) => [f.key, src?.[f.key] == null ? '' : String(src[f.key])]));

// Reusable inline error text under a field. Renders nothing until the
// parent flips `showAllErrors` on failed submit — same UX pattern as the
// Company/User wizards.
const FieldError = ({ show, msg }: { show: boolean; msg?: string }) =>
  show && msg ? (
    <small className="mt-1 block text-xs font-medium text-destructive">{msg}</small>
  ) : null;

function RenderAuthenticationFields(props: any) {
  props = props['props'];
  const [showPassword, setShowPassword] = useState(false);
  const showErr = Boolean(props.showAllErrors);

  const normalizedAuthType =
    typeof props.authType === 'string'
      ? TRUNK_AUTH_TYPES.find(
          (x: any) =>
            x.label.toLowerCase() === props.authType.toLowerCase() ||
            String(x.value) === props.authType,
        )?.value
      : props.authType;

  if (normalizedAuthType === TRUNK_AUTH_TYPE_IP) {
    return (
      <div className="col-md-12 form-group mb-3">
        <label htmlFor="identify_by" className="form-label">
          Identify By: {_requiredStar}
        </label>
        <input
          type="text"
          className="form-control"
          placeholder="Enter valid IPv4"
          value={props.ipIdentifyBy}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.]/g, '');
            props.setIpIdentifyBy(value);
          }}
          onBlur={() => {
            const ipRegex =
              /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

            if (props.ipIdentifyBy && !ipRegex.test(props.ipIdentifyBy)) {
              toast.error('Invalid IPv4 address');
              props.setIpIdentifyBy('');
            }
          }}
          required
        />
        <FieldError
          show={showErr && !(props.ipIdentifyBy || '').trim()}
          msg="Identify By (IPv4) is required"
        />
      </div>
    );
  } else if (normalizedAuthType === TRUNK_AUTH_TYPE_REGISTRATION) {
    return (
      <>
        <div className="col-md-6 form-group mb-3">
          <label htmlFor="username" className="form-label">Username: {_requiredStar}</label>
          <input
            type="text"
            className="form-control"
            id="username"
            placeholder="Username"
            value={props.authUsername}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z0-9]*$/.test(value)) {
                props.setAuthUsername(value);
              }
            }}
            onBlur={() => {
              if (!props.authUsername || !props.authUsername.trim()) {
                toast.error('Username cannot be empty');
              }
            }}
            required
          />
          <FieldError show={showErr && !(props.authUsername || '').trim()} msg="Username is required" />
        </div>
        <div className="col-md-6 form-group mb-3">
          <label htmlFor="password" className="form-label">
            Password: {_requiredStar}
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className="form-control"
              id="password"
              placeholder="Password"
              value={props.authPassword}
              onChange={(e) => props.setAuthPassword(e.target.value)}
              required
              style={{ paddingRight: '40px' }}
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </span>
          </div>
          <FieldError show={showErr && !(props.authPassword || '').trim()} msg="Password is required" />
        </div>
        <div className="col-md-6 form-group mb-3">
          <label htmlFor="server_url" className="form-label">Authentication server url: {_requiredStar}</label>
          <input
            type="text"
            className="form-control"
            placeholder="Authentication server url:"
            id="server_url"
            maxLength={100}
            value={props.authServerUrl}
            onChange={(e) => props.setAuthServerUrl(e.target.value)}
          />
          <FieldError show={showErr && !(props.authServerUrl || '').trim()} msg="Authentication server URL is required" />
        </div>
        <div className="col-md-6 form-group mb-3">
          <label htmlFor="client_url" className="form-label">Authentication client url: {_requiredStar}</label>
          <input
            type="text"
            className="form-control"
            id="client_url"
            placeholder="Authentication client url:"
            maxLength={100}
            value={props.authClientUrl}
            onChange={(e) => props.setAuthClientUrl(e.target.value)}
          />
          <FieldError show={showErr && !(props.authClientUrl || '').trim()} msg="Authentication client URL is required" />
        </div>
      </>
    );
  }

  return <></>;
}

export default function RenderTrunkForm(props: any) {
  const [codecsList, setCodecsList] = useState<any[]>([]);
  const [operatorsList, setOperatorsList] = useState<any[]>([]);
  const [serverList, setServerList] = useState<any[]>([]);
  const [countryList, setCountryList] = useState<any[]>([]);

  useEffect(() => {
    getApiCall(API_CODECS_LIST, {}).then(setCodecsList).catch((err) => console.log(err));
    getApiCall(API_OPERATORS_LIST, {}).then(setOperatorsList).catch((err) => console.log(err));
    getApiCall(API_SERVER_MANAGEMENT_LIST, {}).then(setServerList).catch((err) => console.log(err));
    getApiCall(API_COUNTRY_CODES_LIST, {})
      .then((list) => setCountryList(Array.isArray(list) ? list : []))
      .catch((err) => console.log(err));
  }, []);


  const countryOptions = useMemo(() => {
    const all = countryList.map((c: any) => {
      const calling = formatCallingCode(c.calling_code);
      return {
        value: String(c.code).toUpperCase(),
        label: calling ? `${c.name} (${calling})` : String(c.name),
      };
    });
    if (props.isInternational === TRUNK_TYPE_DOMESTIC) {
      return all.filter((o) => o.value === TRUNK_DOMESTIC_COUNTRY_CODE);
    }
    return all;
  }, [countryList, props.isInternational]);


  const handleTrunkTypeChange = (value: string) => {
    const next = Number(value);
    if (next === props.isInternational) return;
    props.setCountryCodes([]);
    props.setOperator('');
    props.setPrefixes(pickTrunkPrefixes());
    props.setIsInternational(next);
  };

  const trunkTypeOptions = [
    {
      value: TRUNK_TYPE_INTERNATIONAL,
      label: 'International',
      hint: 'Calls to / from other countries. Pick one or more countries.',
    },
    {
      value: TRUNK_TYPE_DOMESTIC,
      label: 'Domestic',
      hint: 'Calls within India only.',
    },
  ];

  const trunkTypeChosen =
    props.isInternational === TRUNK_TYPE_DOMESTIC || props.isInternational === TRUNK_TYPE_INTERNATIONAL;

  const operatorOptions = trunkTypeChosen
    ? operatorsList.filter((op: any) => Number(op.is_international) === props.isInternational)
    : [];

  const selectedAuthTypeValue = props.isEditMode
    ? TRUNK_AUTH_TYPES.find(
        (x: any) => x.label.toLowerCase() === (props.authType || '').toLowerCase(),
      )?.value
    : props.authType;

  const showErr = Boolean(props.showAllErrors);

  return (
    <>
      <div className="row">
        <div className="col-md-6 form-group mb-3">
          <label htmlFor="name" className="form-label">
            Name: {_requiredStar}
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            placeholder="Name"
            maxLength={100}
            value={props.name}
            onChange={props.handleChange}
            required
          />
          {props.error && <small className="mt-1 block text-xs font-medium text-destructive">{props.error}</small>}
          <FieldError show={showErr && !props.error && !(props.name || '').trim()} msg="Name is required" />
        </div>
        <div className="col-md-6 form-group mb-3">
          <label htmlFor="auth_type" className="form-label">
            Authentication Type: {_requiredStar}
          </label>
          <select
            id="auth_type"
            className="form-control form-select p-2"
            required
            value={selectedAuthTypeValue}
            onChange={(e) => {
              if (props.isEditMode) {
                const selectedLabel = TRUNK_AUTH_TYPES.find(
                  (x: any) => x.value === Number(e.target.value),
                )?.label;
                props.setAuthType(selectedLabel || '');
              } else {
                props.setAuthType(e.target.value);
              }
            }}
          >
            <option value="">Select Authentication Type</option>
            {TRUNK_AUTH_TYPES.map((val: any) => (
              <option key={val.value} value={val.value}>
                {val.label}
              </option>
            ))}
          </select>
          <FieldError show={showErr && !props.authType} msg="Authentication type is required" />
        </div>
      </div>
      <div className="row">
        <RenderAuthenticationFields props={props} />
      </div>
      {/* Endpoint — Domain gets 8/12 (URLs are long), Port keeps a narrow 4/12. */}
      <div className="row">
        <div className="col-md-8 form-group mb-3">
          <label htmlFor="domain" className="form-label">
            Domain: {_requiredStar}
          </label>
          <input
            type="text"
            className="form-control"
            id="domain"
            maxLength={200}
            value={props.domain}
            placeholder="https://example.com"
            onChange={(e) => {
              const value = e.target.value.trim();
              if (/^[a-zA-Z0-9:/.-]*$/.test(value)) {
                props.setDomain(value);
              }
            }}
            onBlur={() => {
              if (!props.domain) return;

              const protocolRegex = /^https?:\/\//;
              const trailingSlashRegex = /\/$/;

              if (!protocolRegex.test(props.domain)) {
                toast.error('Domain must start with http:// or https://');
                props.setDomain('');
                return;
              }

              if (trailingSlashRegex.test(props.domain)) {
                toast.error('Domain must not end with a trailing slash (/)');
                props.setDomain('');
                return;
              }

              const domainRegex = /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
              if (!domainRegex.test(props.domain)) {
                toast.error('Enter a valid domain, e.g. https://example.com');
                props.setDomain('');
              }
            }}
            required
          />
          <FieldError show={showErr && !(props.domain || '').trim()} msg="Domain is required" />
        </div>
        <div className="col-md-4 form-group mb-3">
          <label htmlFor="port" className="form-label">
            Port Number: {_requiredStar}
          </label>
          <input
            type="text"
            className="form-control"
            id="port"
            placeholder="Port (1024 - 65535)"
            maxLength={5}
            value={props.port}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 5) props.setPort(value);
            }}
            onBlur={() => {
              const portNumber = Number(props.port);
              if (!Number.isInteger(portNumber) || portNumber < 1024 || portNumber > 65535) {
                toast.error('Port must be between 1024 and 65535');
                props.setPort('');
              }
            }}
            required
          />
          <FieldError
            show={showErr && (!props.port || Number(props.port) < 1024 || Number(props.port) > 65535)}
            msg="Port must be between 1024 and 65535"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 form-group mb-3">
          <label className="form-label">Trunk Type: {_requiredStar}</label>
          <RadioGroup
            value={trunkTypeChosen ? String(props.isInternational) : ''}
            onValueChange={handleTrunkTypeChange}
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {trunkTypeOptions.map((opt) => {
              const selected = props.isInternational === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={`trunk-type-${opt.value}`}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 transition-colors',
                    selected ? 'border-primary ring-1 ring-primary/40' : 'hover:border-ring',
                  )}
                >
                  <RadioGroupItem value={String(opt.value)} id={`trunk-type-${opt.value}`} className="mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{opt.hint}</div>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
          <FieldError show={showErr && !trunkTypeChosen} msg="Trunk type is required" />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 form-group mb-3">
          <label className="form-label">Country Code: {_requiredStar}</label>
          <MultiSelect
            options={countryOptions}
            value={props.countryCodes || []}
            onChange={props.setCountryCodes}
            disabled={!trunkTypeChosen}
            placeholder={trunkTypeChosen ? 'Select country code(s)' : 'Select trunk type first'}
            searchPlaceholder="Search country…"
            emptyMessage="No country found"
            maxChips={4}
          />
          <FieldError
            show={showErr && trunkTypeChosen && !(props.countryCodes || []).length}
            msg="Select at least one country code"
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 form-group mb-3">
          <label htmlFor="codecs" className="form-label">
            Select codecs: {_requiredStar}
          </label>
          <select
            id="codecs"
            className="form-control form-select p-2"
            required
            value={props.codec}
            onChange={(e) => props.setCodec(e.target.value)}
          >
            <option value="">Select codec</option>
            {codecsList.map((val: any, idx: number) => (
              <option key={idx} value={val.id}>
                {val.name}
              </option>
            ))}
          </select>
          <FieldError show={showErr && !props.codec} msg="Codec is required" />
        </div>
        <div className="col-md-4 form-group mb-3">
          <label htmlFor="operator" className="form-label">
            Select Operator: {_requiredStar}
          </label>
          <select
            id="operator"
            className="form-control form-select p-2"
            required
            disabled={!trunkTypeChosen}
            value={props.operator}
            onChange={(e) => {
              props.setOperator(e.target.value);
              const selected = operatorsList.find((op: any) => String(op.id) === e.target.value);
              props.setPrefixes(pickTrunkPrefixes(selected));
            }}
          >
            <option value="">{trunkTypeChosen ? 'Select Operator' : 'Select trunk type first'}</option>
            {operatorOptions.map((val: any, idx: number) => (
              <option key={idx} value={val.id}>
                {val.name}
              </option>
            ))}
          </select>
          <FieldError show={showErr && !props.operator} msg="Operator is required" />
        </div>
        <div className="col-md-4 form-group mb-3">
          <label htmlFor="server" className="form-label">
            Gateway: {_requiredStar}
          </label>
          <select
            id="server"
            className="form-control form-select p-2"
            required
            value={props.server}
            onChange={(e) => props.setServer(e.target.value)}
          >
            <option value="">Select Gateway</option>
            {serverList.map((val: any, idx: number) => (
              <option key={idx} value={val.id}>
                {val.server_name}
              </option>
            ))}
          </select>
          <FieldError show={showErr && !props.server} msg="Gateway is required" />
        </div>
      </div>


      <div className="mb-4 rounded-lg border border-border/60 p-4">
        <div className="mb-3 text-sm font-semibold text-foreground">Caller ID Prefixes</div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {TRUNK_PREFIX_GROUPS.map((group) => (
            <div key={group.title} className="rounded-md border border-border/50 bg-card/40 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.title}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {group.fields.map((f) => (
                  <div key={f.key} className="min-w-0">
                    <label htmlFor={f.key} className="form-label">
                      {f.short}
                    </label>
                    <input
                      type="text"
                      inputMode="tel"
                      className="form-control w-full"
                      id={f.key}
                      placeholder={f.short}
                      maxLength={18}
                      value={props.prefixes?.[f.key] ?? ''}
                      onChange={(e) =>
                        props.setPrefixes({
                          ...props.prefixes,
                          [f.key]: e.target.value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, ''),
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>


      <div className="mb-3 rounded-lg border border-border/60 p-4">
        <div className="mb-3 text-sm font-semibold text-foreground">Capacity</div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="min-w-0">
            <label htmlFor="pilot_number" className="form-label">
              Pilot Number: {_requiredStar}
            </label>
            <input
              type="text"
              className="form-control w-full"
              id="pilot_number"
              maxLength={10}
              value={props.pilotNumber}
              placeholder="Pilot Number (max 10 digits)"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 10) props.setPilotNumber(value);
              }}
              required
            />
            <FieldError
              show={showErr && (!(props.pilotNumber || '').trim() || (props.pilotNumber || '').length < 10)}
              msg="Pilot number must be 10 digits"
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="chanels" className="form-label">
              Channels: {_requiredStar}
            </label>
            <input
              type="number"
              className="form-control w-full"
              id="chanels"
              placeholder="Channels"
              min={0}
              max={10000}
              value={props.channels}
              onChange={(e) => {
                const value: any = e.target.value;
                if (value === '' || (value >= 0 && value <= 10000)) props.setChannels(value);
              }}
              required
            />
            <FieldError
              show={showErr && (props.channels === '' || props.channels == null || Number(props.channels) < 0 || Number(props.channels) > 10000)}
              msg="Channels must be between 0 and 10000"
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="cps" className="form-label">
              Calls per sec: {_requiredStar}
            </label>
            <input
              type="number"
              className="form-control w-full"
              id="cps"
              placeholder="Calls per sec"
              min={0}
              max={1000}
              value={props.cps}
              onChange={(e) => {
                const value: any = e.target.value;
                if (value === '' || (value >= 0 && value <= 1000)) props.setCps(value);
              }}
              required
            />
            <FieldError
              show={showErr && (props.cps === '' || props.cps == null || Number(props.cps) < 0 || Number(props.cps) > 1000)}
              msg="Calls per sec must be between 0 and 1000"
            />
          </div>
        </div>
      </div>
    </>
  );
}
